<template>
  <div
    class="bg-gray-50 dark:bg-gray-900 flex items-center justify-center flex-grow py-12 px-4 sm:px-6 lg:px-8"
  >
    <div class="max-w-md w-full space-y-8">
      <div>
        <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
          Sign in to your account
        </h2>
        <p
          v-if="!checkingRegistration && registrationEnabled"
          class="mt-2 text-center text-sm text-gray-600 dark:text-gray-400"
        >
          Or
          <router-link
            to="/register"
            class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            create a new account
          </router-link>
        </p>
      </div>

      <form
        class="mt-8 space-y-6"
        @submit.prevent="challengeToken ? handleTwoFactor() : handleLogin()"
      >
        <div v-if="challengeToken" class="space-y-4">
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Enter the 6-digit code from your authenticator app, or a recovery code.
          </p>
          <div>
            <label for="code" class="sr-only">Authentication code</label>
            <input
              id="code"
              v-model="code"
              name="code"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              required
              class="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Authentication code"
            />
          </div>
        </div>
        <div v-else class="rounded-md shadow-sm -space-y-px">
          <div>
            <label for="email" class="sr-only">Email address</label>
            <input
              id="email"
              v-model="form.email"
              name="email"
              type="email"
              autocomplete="email"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Email address"
            />
          </div>
          <div>
            <label for="password" class="sr-only">Password</label>
            <input
              id="password"
              v-model="form.password"
              name="password"
              type="password"
              autocomplete="current-password"
              required
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Password"
            />
          </div>
        </div>

        <div v-if="error" class="text-red-600 dark:text-red-400 text-sm text-center">
          {{ error }}
        </div>

        <div class="flex items-center justify-between">
          <div class="text-sm">
            <router-link
              to="/forgot-password"
              class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Forgot your password?
            </router-link>
          </div>
        </div>

        <div class="space-y-3">
          <Button
            type="submit"
            :disabled="loading"
            :label="submitLabel"
            severity="primary"
            :loading="loading"
            class="w-full"
          />
          <button
            v-if="challengeToken"
            type="button"
            class="w-full text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
            @click="backToPassword"
          >
            Back
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../../stores/auth";
import api from "../../services/api";
import { getHelperLivePath } from "../../utils/eventStatus";
import Button from "primevue/button";

export default {
  name: "Login",
  components: {
    Button
  },
  setup() {
    const router = useRouter();
    const authStore = useAuthStore();

    const form = ref({
      email: "",
      password: ""
    });
    const challengeToken = ref("");
    const code = ref("");
    const error = ref("");
    const loading = ref(false);
    const submitLabel = computed(() => {
      if (loading.value) return challengeToken.value ? "Verifying..." : "Signing in...";
      return challengeToken.value ? "Verify" : "Sign in";
    });
    const registrationEnabled = ref(false); // Start as false, update after check
    const checkingRegistration = ref(true); // Track loading state

    const checkRegistrationStatus = async () => {
      try {
        checkingRegistration.value = true;
        const response = await api.get("/api/settings/registration/public");
        console.log("Registration status response:", response.data);
        if (response.data.success) {
          registrationEnabled.value = response.data.data.registrationEnabled;
          console.log("Registration enabled:", registrationEnabled.value);
        } else {
          console.warn("Registration status check failed:", response.data.message);
          registrationEnabled.value = true; // Default to enabled if check fails
        }
      } catch (err) {
        // If we can't check the status, default to enabled (show link)
        console.error("Could not check registration status:", err);
        console.error("Error details:", err.response?.data || err.message);
        registrationEnabled.value = true;
      } finally {
        checkingRegistration.value = false;
      }
    };

    const handleLogin = async () => {
      try {
        loading.value = true;
        error.value = "";

        console.log("Attempting login with:", form.value);
        const result = await authStore.login(form.value);
        console.log("Login result:", result);

        if (result.success && result.twoFactorRequired) {
          challengeToken.value = result.challengeToken;
          code.value = "";
          return;
        }
        if (result.success) {
          await finishLogin(result.mustChangePassword);
        } else {
          error.value = result.message || "Login failed";
        }
      } catch (err) {
        console.error("Login error:", err);
        error.value = "An unexpected error occurred";
      } finally {
        loading.value = false;
      }
    };

    const handleTwoFactor = async () => {
      try {
        loading.value = true;
        error.value = "";
        const result = await authStore.completeTwoFactor({
          challengeToken: challengeToken.value,
          code: code.value
        });
        if (result.success) {
          challengeToken.value = "";
          await finishLogin(result.mustChangePassword);
        } else {
          error.value = result.message || "Invalid authentication code";
        }
      } catch (err) {
        console.error("Two-factor login error:", err);
        error.value = "An unexpected error occurred";
      } finally {
        loading.value = false;
      }
    };

    const finishLogin = async (mustChangePassword) => {
      if (mustChangePassword) {
        return;
      }
      if (authStore.user?.role === "helper") {
        try {
          const response = await api.get("/api/events");
          if (response.data.success) {
            const events = response.data.data.events || [];
            router.push(getHelperLivePath(events));
          } else {
            router.push("/events/live");
          }
        } catch {
          router.push("/events/live");
        }
        return;
      }
      router.push("/");
    };

    const backToPassword = () => {
      challengeToken.value = "";
      code.value = "";
      error.value = "";
    };

    onMounted(() => {
      checkRegistrationStatus();
    });

    return {
      form,
      challengeToken,
      code,
      error,
      loading,
      submitLabel,
      registrationEnabled,
      checkingRegistration,
      handleLogin,
      handleTwoFactor,
      backToPassword
    };
  }
};
</script>
